import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import FreezingRequestServices from "../../UserServices/FreezingRequestServices";
import logo from "../../icons/logo.png";
import "./PublicFreezingRequest.css";

const TODAY = () => new Date().toLocaleDateString("en-CA");

export default function PublicFreezingRequest() {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    membership_uid: "",
    email: "",
    branch_id: "",
  });
  const [verified, setVerified] = useState(null);
  const [verifyError, setVerifyError] = useState("");
  const [packageRef, setPackageRef] = useState("");
  const [startDate, setStartDate] = useState(TODAY());
  const [duration, setDuration] = useState(7);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [done, setDone] = useState(null);
  const verifySeq = useRef(0);

  useEffect(() => {
    FreezingRequestServices.publicBranches()
      .then((res) => setBranches(res.data?.data || []))
      .catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    setVerified(null);
    setVerifyError("");
    setPackageRef("");
    setDone(null);
  }, [form.full_name, form.membership_uid, form.email, form.branch_id]);

  const canVerify = useMemo(
    () =>
      form.full_name.trim().length >= 2 &&
      form.membership_uid.trim().length >= 6 &&
      form.email.includes("@") &&
      form.branch_id,
    [form]
  );

  const canApply =
    verified?.validation_token &&
    packageRef &&
    startDate &&
    Number(duration) >= 2 &&
    Number(duration) <= 100;

  const runVerify = useCallback(
    async (silent = false) => {
      if (!canVerify) return;
      const seq = ++verifySeq.current;
      setVerifying(true);
      setVerifyError("");
      try {
        const res = await FreezingRequestServices.publicValidate({
          full_name: form.full_name.trim(),
          membership_uid: form.membership_uid.trim(),
          email: form.email.trim(),
          branch_id: parseInt(form.branch_id, 10),
        });
        if (seq !== verifySeq.current) return;
        setVerified(res.data?.data || null);
        if (!silent) toast.success(res.data?.message || "Verified");
      } catch (err) {
        if (seq !== verifySeq.current) return;
        setVerified(null);
        const msg = err.response?.data?.message || "Verification failed";
        setVerifyError(msg);
        if (!silent) toast.error(msg);
      } finally {
        if (seq === verifySeq.current) setVerifying(false);
      }
    },
    [canVerify, form]
  );

  useEffect(() => {
    if (!canVerify) return undefined;
    const t = setTimeout(() => {
      runVerify(true);
    }, 650);
    return () => clearTimeout(t);
  }, [
    canVerify,
    form.full_name,
    form.membership_uid,
    form.email,
    form.branch_id,
    runVerify,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canApply) return;
    setBusy(true);
    try {
      const res = await FreezingRequestServices.publicSubmit({
        validation_token: verified.validation_token,
        package_ref: packageRef,
        requested_start_date: startDate,
        duration_days: parseInt(duration, 10),
        reason: reason.trim() || undefined,
      });
      setDone(res.data?.data || { status: "pending" });
      toast.success(res.data?.message || "Submitted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pfr-page">
        <div className="pfr-card">
          <img src={logo} alt="Vostro World" className="pfr-logo" />
          <h2>Request submitted</h2>
          <p>
            Your freezing request is <strong>{done.status}</strong>. Our team will
            review it. Reference #{done.request_id}.
          </p>
          <p className="pfr-muted">
            You can close this page. For questions, visit the reception counter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pfr-page">
      <div className="pfr-card">
        <img src={logo} alt="Vostro World" className="pfr-logo" />
        <h1>Freezing Request</h1>
        <p className="pfr-lead">
          Fill using details from your membership card. We verify your identity
          before you can apply.
        </p>

        <div className="pfr-guide">
          <strong>How to fill (from your card)</strong>
          <ul>
            <li>
              <b>Full name</b> — exactly as printed (e.g. Sheikh Asad).
            </li>
            <li>
              <b>Membership number</b> — the <b>Member ID</b> (example:{" "}
              <code>F11240871</code>). No spaces.
            </li>
            <li>
              <b>Email</b> — the email registered on your membership profile.
            </li>
            <li>
              <b>Branch / Outlet</b> — same as “Outlet” on your card (e.g. F 11).
            </li>
          </ul>
        </div>

        <div className="pfr-fields">
          <label>
            Full name
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="As on card"
              autoComplete="name"
            />
          </label>
          <label>
            Membership number (Member ID)
            <input
              value={form.membership_uid}
              onChange={(e) =>
                setForm({
                  ...form,
                  membership_uid: e.target.value.toUpperCase().replace(/\s/g, ""),
                })
              }
              placeholder="e.g. F11240871"
              autoComplete="off"
            />
            <span className="pfr-hint">Copy Member ID from your Vostro card.</span>
          </label>
          <label>
            Registered email
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </label>
          <label>
            Branch (Outlet)
            <select
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
            >
              <option value="">Select outlet</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="pfr-verify-row">
          {verifying ? (
            <span className="pfr-status checking">Checking details…</span>
          ) : verified ? (
            <span className="pfr-status ok">
              Identity verified — select package below
            </span>
          ) : verifyError ? (
            <span className="pfr-status bad">{verifyError}</span>
          ) : canVerify ? (
            <span className="pfr-status muted">Waiting for verification…</span>
          ) : (
            <span className="pfr-status muted">Complete all fields to verify</span>
          )}
          <button
            type="button"
            className="pfr-btn outline"
            disabled={!canVerify || verifying || busy}
            onClick={() => runVerify(false)}
          >
            Verify now
          </button>
        </div>

        {verified ? (
          <form onSubmit={submit} className="pfr-apply">
            <label>
              Package to freeze
              <select
                value={packageRef}
                onChange={(e) => setPackageRef(e.target.value)}
                required
              >
                <option value="">Select package</option>
                {(verified.packages || []).map((p) => (
                  <option key={p.ref} value={p.ref}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="pfr-row2">
              <label>
                Start date
                <input
                  type="date"
                  min={TODAY()}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </label>
              <label>
                Duration (days)
                <input
                  type="number"
                  min={2}
                  max={100}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                />
              </label>
            </div>
            <label>
              Reason (optional)
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            </label>
            <button
              type="submit"
              className="pfr-btn primary"
              disabled={!canApply || busy}
            >
              {busy ? "Submitting…" : "Apply for freezing"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
